/**
 * Authentication Module
 * Handles Google OAuth, user sessions, and profile management
 */

import { supabase } from './supabaseClient.js';

class AuthManager {
  constructor() {
    this.user = null;
    this.session = null;
    this.initialized = false;
  }

  /**
   * Initialize authentication state
   */
  async init() {
    try {
      // Check if Supabase is configured
      if (!supabase) {
        console.warn('Supabase not configured. Authentication disabled.');
        this.initialized = true;
        this.updateUI();
        return null;
      }

      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      this.session = session;
      this.user = session?.user || null;

      // If no user, sign in anonymously to enable Supabase features without signups
      if (!this.user) {
        console.log('No session found. Signing in anonymously...');
        const { data: anonData, error: anonError } = await supabase.auth.signInAnonymously();
        if (anonError) throw anonError;
        this.session = anonData.session;
        this.user = anonData.user;
      }

      // Listen for auth state changes
      // IMPORTANT: Never await Supabase data operations inside this callback —
      // the GoTrueClient awaits this callback internally, so awaiting a Supabase
      // request here creates a deadlock (client waits for callback, callback waits
      // for client). Use fire-and-forget instead.
      supabase.auth.onAuthStateChange((event, session) => {
        console.log('Auth state changed:', event);
        this.session = session;
        this.user = session?.user || null;

        if (event === 'SIGNED_IN' && this.user && !this.user.is_anonymous) {
          this.syncUserProfile().catch(e => console.warn('Profile sync failed:', e));
        }

        // Update UI
        this.updateUI();
      });

      this.initialized = true;
      this.updateUI();

      return this.user;
    } catch (error) {
      console.error('Auth initialization error:', error);
      this.initialized = true;
      this.updateUI();
      return null;
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  }

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) throw error;

      this.user = null;
      this.session = null;
      this.updateUI();
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  }

  /**
   * Sync user profile to database
   */
  async syncUserProfile() {
    if (!this.user) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .upsert({
          id: this.user.id,
          email: this.user.email,
          display_name: this.user.user_metadata?.full_name || this.user.email?.split('@')[0],
          avatar_url: this.user.user_metadata?.avatar_url,
          google_id: this.user.user_metadata?.sub,
          last_seen_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        })
        .select()
        .single();

      if (error) throw error;

      console.log('User profile synced:', data);
      return data;
    } catch (error) {
      console.error('Profile sync error:', error);
      throw error;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.user;
  }

  /**
   * Update UI based on auth state
   */
  updateUI() {
    const authButton = document.getElementById('auth-button');
    const userAvatar = document.getElementById('user-avatar');

    if (!authButton) return;

    if (this.isAuthenticated()) {
      // User is logged in
      const userName = this.user.user_metadata?.full_name || this.user.email?.split('@')[0];
      const avatarUrl = this.user.user_metadata?.avatar_url;

      authButton.innerHTML = `
        <div class="user-menu">
          ${avatarUrl ? `
            <img src="${avatarUrl}" alt="${userName}" class="user-avatar">
          ` : `
            <div class="user-avatar-placeholder">${userName?.charAt(0).toUpperCase()}</div>
          `}
          <span class="user-name">${userName}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      `;

      // Add dropdown menu
      this.setupUserMenu();
    } else {
      // User is logged out
      authButton.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
        <span>Sign In</span>
      `;

      authButton.onclick = () => this.signInWithGoogle();
    }
  }

  /**
   * Setup user dropdown menu
   */
  setupUserMenu() {
    const authButton = document.getElementById('auth-button');

    if (!authButton) return;

    authButton.onclick = (e) => {
      e.stopPropagation();

      // Toggle dropdown
      let dropdown = document.getElementById('user-dropdown');

      if (dropdown) {
        dropdown.remove();
      } else {
        dropdown = document.createElement('div');
        dropdown.id = 'user-dropdown';
        dropdown.className = 'user-dropdown';
        dropdown.innerHTML = `
          <button class="dropdown-item" id="profile-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Profile
          </button>
          <button class="dropdown-item" id="favorites-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            Favorites
          </button>
          <div class="dropdown-divider"></div>
          <button class="dropdown-item" id="signout-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Sign Out
          </button>
        `;

        authButton.parentElement.appendChild(dropdown);

        // Add event listeners
        document.getElementById('signout-btn').onclick = () => {
          this.signOut();
          dropdown.remove();
        };

        document.getElementById('favorites-btn').onclick = (e) => {
          e.stopPropagation();
          // Filter gallery to show only liked wallpapers
          window.dispatchEvent(new CustomEvent('showFavorites'));
          dropdown.remove();
        };

        document.getElementById('profile-btn').onclick = (e) => {
          e.stopPropagation();
          // Close dropdown (profile page coming soon)
          dropdown.remove();
        };

        // Close dropdown when clicking outside
        setTimeout(() => {
          document.addEventListener('click', function closeDropdown() {
            dropdown?.remove();
            document.removeEventListener('click', closeDropdown);
          });
        }, 0);
      }
    };
  }
}

// Export singleton instance
export const authManager = new AuthManager();
export default authManager;
